/**
 * Integrated Budgets Hooks
 * 
 * Hooks for managing budgets integrated with scenarios
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '../utils/supabase/info';

const API_URL = `https://${projectId}.supabase.co/functions/v1/make-server-36aa765f`;

// ============================================
// TYPES
// ============================================

export type ActualsMode = 'auto' | 'confirmed_only' | 'forecast_only' | 'both';

export interface IntegratedBudget {
  id: string;
  company_id: string;
  scenario_id?: string | null;
  combined_scenario_ids?: string[] | null;
  name: string;
  description?: string | null;
  period_type: 'monthly' | 'quarterly' | 'yearly';
  start_date: string;
  end_date: string;
  is_active: boolean;
  sync_mode: 'manual' | 'notify' | 'auto';
  actuals_mode?: ActualsMode;
  created_from_scenario_at?: string | null;
  last_synced_at?: string | null;
  created_at: string;
  updated_at: string;
  items?: BudgetItem[];
  alerts?: BudgetAlert[];
}

export interface BudgetItem {
  id: string;
  budget_id: string;
  category_id?: string | null;
  planned_amount: number;
  actual_amount: number;
  variance: number;
  variance_percent: number;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface BudgetAlert {
  id: string;
  budget_id: string;
  budget_item_id?: string | null;
  type: 'overspent' | 'underspent' | 'warning' | 'sync_needed';
  severity: 'low' | 'medium' | 'high';
  message: string;
  threshold_percent?: number | null;
  current_percent?: number | null;
  metadata?: Record<string, any>;
  acknowledged: boolean;
  acknowledged_at?: string | null;
  acknowledged_by?: string | null;
  created_at: string;
}

export interface CreateBudgetData {
  company_id: string;
  name: string;
  description?: string;
  start_date: string;
  end_date: string;
  period_type?: 'monthly' | 'quarterly' | 'yearly';
  items?: Array<{
    category_id?: string;
    planned_amount: number;
    notes?: string;
  }>;
}

export interface GenerateFromScenarioData {
  scenario_id: string;
  company_id: string;
  year: number;
  name?: string;
}

export interface GenerateFromMultipleScenariosData {
  scenario_ids: string[];
  company_id: string;
  year: number;
  name?: string;
}

// ============================================
// HOOKS
// ============================================

/**
 * Get all budgets for a company
 */
export function useIntegratedBudgets(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['integrated-budgets', companyId],
    queryFn: async () => {
      if (!companyId) return { budgets: [] };
      
      console.log('[useIntegratedBudgets] Fetching budgets for company:', companyId);
      
      try {
        const response = await fetch(`${API_URL}/integrated-budgets/${companyId}`, {
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,
          },
          signal: AbortSignal.timeout(150000), // 150s timeout - 2.5min para dar tempo do cold start
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('[useIntegratedBudgets] Error response:', response.status, errorData);
          
          // Don't throw on 404 - just return empty
          if (response.status === 404) {
            console.log('[useIntegratedBudgets] No budgets found (404), returning empty array');
            return { budgets: [] };
          }
          
          throw new Error(`Failed to fetch budgets: ${errorData.error || response.statusText}`);
        }
        
        const data = await response.json();
        console.log('[useIntegratedBudgets] Successfully fetched budgets:', data.budgets?.length || 0);
        return data;
      } catch (error: any) {
        console.error('[useIntegratedBudgets] Fetch error:', error);
        
        // If it's a timeout or network error, return empty instead of failing - SEM TOAST
        if (error.name === 'TimeoutError' || error.message?.includes('Failed to fetch')) {
          console.warn('[useIntegratedBudgets] ⚠️ Timeout/Network error - retornando vazio (graceful degradation)');
          // NÃO MOSTRA TOAST - apenas retorna vazio para não incomodar o usuário
          return { budgets: [] };
        }
        
        throw error;
      }
    },
    enabled: !!companyId,
    retry: 1, // Apenas 1 retry para não sobrecarregar
    retryDelay: 3000, // 3s entre tentativas
    staleTime: 120000, // 2min - cache mais longo
    gcTime: 600000, // 10min - mantém em cache por mais tempo
  });
}

/**
 * Get a single budget with details
 */
export function useIntegratedBudget(budgetId: string | null | undefined) {
  return useQuery({
    queryKey: ['integrated-budget', budgetId],
    queryFn: async () => {
      if (!budgetId) return null;
      
      const response = await fetch(`${API_URL}/integrated-budgets/detail/${budgetId}`, {
        headers: {
          'Authorization': `Bearer ${publicAnonKey}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch budget');
      }
      
      const data = await response.json();
      return data.budget as IntegratedBudget;
    },
    enabled: !!budgetId,
  });
}

/**
 * Get active budget for a company
 */
export function useActiveBudget(companyId: string | null | undefined) {
  const { data } = useIntegratedBudgets(companyId);
  
  return {
    data: data?.budgets?.find((b: IntegratedBudget) => b.is_active) || null,
  };
}

/**
 * Create a budget manually
 */
export function useCreateBudget() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: CreateBudgetData) => {
      const response = await fetch(`${API_URL}/integrated-budgets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create budget');
      }
      
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets', variables.company_id] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets-direct'] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budget-direct'] });
      toast.success('Orçamento criado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao criar orçamento');
    },
  });
}

/**
 * Generate budget from a single scenario
 */
export function useGenerateBudgetFromScenario() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: GenerateFromScenarioData) => {
      const response = await fetch(`${API_URL}/integrated-budgets/generate-from-scenario`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${publicAnonKey}`,
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate budget');
      }
      
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets', variables.company_id] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets-direct'] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budget-direct'] });
      toast.success('Orçamento gerado com sucesso do cenário!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao gerar orçamento');
    },
  });
}

/**
 * Generate budget from multiple scenarios (COMBINED)
 */
export function useGenerateBudgetFromMultipleScenarios() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: GenerateFromMultipleScenariosData) => {
      console.log('[useGenerateBudgetFromMultipleScenarios] Starting request...');
      
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000); // 90s timeout for cold start
      
      try {
        const response = await fetch(`${API_URL}/integrated-budgets/generate-from-multiple-scenarios`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${publicAnonKey}`,
          },
          body: JSON.stringify(data),
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        console.log('[useGenerateBudgetFromMultipleScenarios] Response status:', response.status);
      
        if (!response.ok) {
          const error = await response.json();
          console.error('Server error generating budget:', error);
          
          // Check if this is a migration issue
          if (error.needsMigration) {
            const errorMsg = `⚠️ Database Setup Required\n\n${error.details}\n\nPlease check the console for migration instructions.`;
            console.error('\n==============================================');
            console.error('🔴 DATABASE MIGRATION REQUIRED');
            console.error('==============================================');
            console.error('The budget tables do not exist in your database.');
            console.error('Please run the migration file:');
            console.error('  → /database/migrations/create_budgets_tables.sql');
            console.error('\nHow to run:');
            console.error('  1. Go to your Supabase project dashboard');
            console.error('  2. Navigate to SQL Editor');
            console.error('  3. Copy/paste the contents of create_budgets_tables.sql');
            console.error('  4. Click Run');
            console.error('==============================================\n');
            throw new Error(errorMsg);
          }
          
          const errorMsg = error.details 
            ? `${error.error}: ${error.details}${error.errorHint ? ` (${error.errorHint})` : ''}`
            : error.error || 'Failed to generate combined budget';
          throw new Error(errorMsg);
        }
        
        console.log('[useGenerateBudgetFromMultipleScenarios] Request successful');
        return response.json();
      } catch (error: any) {
        clearTimeout(timeoutId);
        
        // Handle timeout/abort errors
        if (error.name === 'AbortError') {
          console.error('[useGenerateBudgetFromMultipleScenarios] Request timeout (90s)');
          throw new Error('Requisição expirou. O servidor pode estar iniciando (cold start). Tente novamente em alguns segundos.');
        }
        
        // Handle network errors
        if (error.message === 'Failed to fetch') {
          console.error('[useGenerateBudgetFromMultipleScenarios] Network error');
          throw new Error('Erro de conexão. Verifique sua internet ou tente novamente em alguns segundos (servidor pode estar iniciando).');
        }
        
        // Re-throw other errors
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      // Invalidate all budget-related queries
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets', variables.company_id] });
      queryClient.invalidateQueries({ queryKey: ['active-budget', variables.company_id] });
      queryClient.invalidateQueries({ queryKey: ['budget-alerts', variables.company_id] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets-direct'] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budget-direct'] });
      toast.success('Orçamento combinado gerado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao gerar orçamento combinado');
    },
  });
}

/**
 * Sync budget with scenario (regenerate)
 */
export function useSyncBudget() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (budgetId: string) => {
      const response = await fetch(`${API_URL}/integrated-budgets/sync/${budgetId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${publicAnonKey}`,
        },
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to sync budget');
      }
      
      return response.json();
    },
    onSuccess: (_, budgetId) => {
      queryClient.invalidateQueries({ queryKey: ['integrated-budget', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets-direct'] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budget-direct'] });
      toast.success('Orçamento sincronizado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao sincronizar orçamento');
    },
  });
}

/**
 * Update budget actuals from transactions
 */
export function useUpdateBudgetActuals() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (budgetId: string) => {
      console.log('[useUpdateBudgetActuals] Starting update for budget:', budgetId);
      
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000); // 90s timeout
      
      try {
        const response = await fetch(`${API_URL}/integrated-budgets/update-actuals/${budgetId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,
          },
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        console.log('[useUpdateBudgetActuals] Response status:', response.status);
        
        if (!response.ok) {
          const error = await response.json();
          console.error('[useUpdateBudgetActuals] Server error:', error);
          
          // Handle budget not found
          if (error.code === 'BUDGET_NOT_FOUND' || response.status === 404) {
            throw new Error('Orçamento não encontrado. Pode ter sido excluído. Recarregue a página.');
          }
          
          const errorMsg = error.details 
            ? `${error.error}: ${error.details}`
            : error.error || 'Failed to update actuals';
          throw new Error(errorMsg);
        }
        
        const data = await response.json();
        console.log('[useUpdateBudgetActuals] Successfully updated actuals');
        return data;
      } catch (error: any) {
        clearTimeout(timeoutId);
        
        // Handle timeout/abort errors
        if (error.name === 'AbortError') {
          console.error('[useUpdateBudgetActuals] Request timeout (90s)');
          throw new Error('Requisição expirou. O servidor pode estar iniciando (cold start). Tente novamente em alguns segundos.');
        }
        
        // Handle network errors
        if (error.message === 'Failed to fetch') {
          console.error('[useUpdateBudgetActuals] Network error');
          throw new Error('Erro de conexão. Verifique sua internet ou tente novamente em alguns segundos (servidor pode estar iniciando).');
        }
        
        // Re-throw other errors
        throw error;
      }
    },
    onSuccess: (data, budgetId) => {
      console.log('[useUpdateBudgetActuals] Invalidating queries for budget:', budgetId);
      
      // ✅ CORREÇÃO: Invalidar E forçar refetch em seguida
      queryClient.invalidateQueries({ queryKey: ['integrated-budget', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets'] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budget-direct', budgetId] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets-direct'] });
      
      // Força refetch imediato (não aguarda, executa em background)
      setTimeout(() => {
        console.log('[useUpdateBudgetActuals] Forcing refetch...');
        queryClient.refetchQueries({ queryKey: ['integrated-budget-direct', budgetId] });
        queryClient.refetchQueries({ queryKey: ['integrated-budgets-direct'] });
        console.log('[useUpdateBudgetActuals] ✅ Refetch triggered!');
      }, 100);
      
      // Show success message only if called manually (not auto-update)
      if (data?.payment_statuses_used) {
        console.log('[useUpdateBudgetActuals] Payment statuses used:', data.payment_statuses_used);
      }
    },
    onError: (error: Error) => {
      console.error('[useUpdateBudgetActuals] Error:', error);
      toast.error(error.message || 'Erro ao atualizar valores reais');
    },
  });
}

/**
 * Get budget alerts for a company
 */
export function useBudgetAlerts(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['budget-alerts', companyId],
    queryFn: async () => {
      if (!companyId) return { alerts: [] };
      
      console.log('[useBudgetAlerts] Fetching alerts for company:', companyId);
      
      try {
        const response = await fetch(`${API_URL}/integrated-budgets/alerts/${companyId}`, {
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`,
          },
          signal: AbortSignal.timeout(60000), // 60s timeout
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('[useBudgetAlerts] Error response:', response.status, errorData);
          
          // Don't throw on 404 - just return empty
          if (response.status === 404) {
            console.log('[useBudgetAlerts] No alerts found (404), returning empty array');
            return { alerts: [] };
          }
          
          throw new Error(`Failed to fetch alerts: ${errorData.error || response.statusText}`);
        }
        
        const data = await response.json();
        console.log('[useBudgetAlerts] Successfully fetched alerts:', data.alerts?.length || 0);
        return data;
      } catch (error: any) {
        console.error('[useBudgetAlerts] Fetch error:', error);
        
        // If it's a timeout or network error, return empty instead of failing
        if (error.name === 'TimeoutError' || error.message?.includes('Failed to fetch')) {
          console.warn('[useBudgetAlerts] Network/timeout error, returning empty alerts array');
          return { alerts: [] };
        }
        
        throw error;
      }
    },
    enabled: !!companyId,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    staleTime: 30000,
    refetchInterval: false, // Don't auto-refetch to reduce load
  });
}

/**
 * Acknowledge an alert
 */
export function useAcknowledgeAlert() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (alertId: string) => {
      const response = await fetch(`${API_URL}/integrated-budgets/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${publicAnonKey}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to acknowledge alert');
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget-alerts'] });
    },
  });
}

/**
 * Check if budget is outdated (needs sync)
 */
export function useBudgetOutdated(budget: IntegratedBudget | null) {
  if (!budget) return { isOutdated: false };
  
  const scenarioId = budget.scenario_id;
  const combinedIds = budget.combined_scenario_ids;
  
  // TODO: Implement actual check against scenario updated_at
  // For now, return false
  return {
    isOutdated: false,
    scenarioId,
    combinedIds,
  };
}

/**
 * Get budget usage for a category
 */
export function useCategoryBudgetUsage(
  companyId: string | null | undefined, 
  categoryId: string | null | undefined
) {
  const { data: activeBudget } = useActiveBudget(companyId);
  
  // Helper to extract category from notes (temporary workaround)
  const extractCategoryFromNotes = (notes?: string | null): string | null => {
    if (!notes) return null;
    const match = notes.match(/Categoria:\s*([^|]+)/);
    return match && match[1] ? match[1].trim() : null;
  };
  
  const usage = activeBudget?.items?.find(item => {
    // Try category_id first (if migration was run)
    if (item.category_id === categoryId) return true;
    
    // Try extracting from notes (current workaround)
    const extractedCategory = extractCategoryFromNotes(item.notes);
    if (extractedCategory === categoryId) return true;
    
    return false;
  });
  
  if (!usage) {
    return {
      planned: 0,
      actual: 0,
      remaining: 0,
      percent: 0,
      status: 'no-budget' as const,
    };
  }
  
  const percent = usage.planned_amount > 0 
    ? (usage.actual_amount / usage.planned_amount) * 100 
    : 0;
  
  const status: 'ok' | 'warning' | 'danger' | 'no-budget' = 
    percent < 80 ? 'ok' :
    percent < 100 ? 'warning' :
    'danger';
  
  return {
    planned: usage.planned_amount,
    actual: usage.actual_amount,
    remaining: usage.planned_amount - usage.actual_amount,
    percent,
    status,
  };
}

/**
 * Delete budget (permanently)
 */
export function useDeleteBudget() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ budgetId, companyId }: { budgetId: string; companyId: string }) => {
      const response = await fetch(`${API_URL}/integrated-budgets/${budgetId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${publicAnonKey}`,
        },
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete budget');
      }
      
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets', variables.companyId] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets-direct'] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budget-direct'] });
      toast.success('Orçamento excluído com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao excluir orçamento');
    },
  });
}

/**
 * Archive budget (set inactive)
 */
export function useArchiveBudget() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ budgetId, companyId }: { budgetId: string; companyId: string }) => {
      const response = await fetch(`${API_URL}/integrated-budgets/${budgetId}/archive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${publicAnonKey}`,
        },
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to archive budget');
      }
      
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets', variables.companyId] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets-direct'] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budget-direct'] });
      toast.success('Orçamento arquivado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao arquivar orçamento');
    },
  });
}

/**
 * Unarchive budget (set active)
 */
export function useUnarchiveBudget() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ budgetId, companyId }: { budgetId: string; companyId: string }) => {
      const response = await fetch(`${API_URL}/integrated-budgets/${budgetId}/unarchive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${publicAnonKey}`,
        },
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to unarchive budget');
      }
      
      return response.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets', variables.companyId] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets-direct'] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budget-direct'] });
      toast.success('Orçamento reativado com sucesso!');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erro ao reativar orçamento');
    },
  });
}

/**
 * Helper function for fetch with retry logic
 */
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries: number = 3,
  baseDelay: number = 1000
): Promise<Response> {
  let lastError: Error | null = null;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      console.log(`[fetchWithRetry] Attempt ${attempt + 1}/${maxRetries}`);
      const response = await fetch(url, options);
      console.log(`[fetchWithRetry] Success on attempt ${attempt + 1}`);
      return response;
    } catch (error: any) {
      lastError = error;
      console.error(`[fetchWithRetry] Attempt ${attempt + 1} failed:`, error.message);
      
      // Don't retry if it's an AbortError (timeout)
      if (error.name === 'AbortError') {
        throw error;
      }
      
      // If not the last attempt, wait before retrying
      if (attempt < maxRetries - 1) {
        const delay = baseDelay * Math.pow(2, attempt); // Exponential backoff
        console.log(`[fetchWithRetry] Waiting ${delay}ms before retry...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  // All attempts failed
  throw lastError || new Error('All retry attempts failed');
}

/**
 * Update budget actuals mode
 */
export function useUpdateActualsMode() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ budgetId, companyId, mode }: { budgetId: string; companyId: string; mode: ActualsMode }) => {
      console.log('[useUpdateActualsMode] Updating mode to:', mode, 'for budget:', budgetId);
      
      const url = `${API_URL}/integrated-budgets/${budgetId}/actuals-mode`;
      console.log('[useUpdateActualsMode] URL:', url);
      console.log('[useUpdateActualsMode] Method: PATCH');
      console.log('[useUpdateActualsMode] Body:', { actuals_mode: mode });
      
      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000); // 90s timeout
      
      try {
        console.log('[useUpdateActualsMode] Starting fetch with retry...');
        const response = await fetchWithRetry(
          url,
          {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${publicAnonKey}`,
            },
            body: JSON.stringify({ actuals_mode: mode }),
            signal: controller.signal,
          },
          3, // Max 3 retries
          1000 // Initial delay of 1s
        );
        console.log('[useUpdateActualsMode] Fetch completed');
        
        clearTimeout(timeoutId);
        
        console.log('[useUpdateActualsMode] Response status:', response.status);
        
        if (!response.ok) {
          const error = await response.json();
          console.error('[useUpdateActualsMode] Server error:', error);
          
          // Handle budget not found
          if (error.code === 'BUDGET_NOT_FOUND' || response.status === 404) {
            throw new Error('Orçamento não encontrado. Pode ter sido excluído. Recarregue a página.');
          }
          
          const errorMsg = error.details 
            ? `${error.error}: ${error.details}`
            : error.error || 'Failed to update actuals mode';
          throw new Error(errorMsg);
        }
        
        const data = await response.json();
        console.log('[useUpdateActualsMode] Successfully updated actuals mode');
        return data;
      } catch (error: any) {
        clearTimeout(timeoutId);
        
        console.error('[useUpdateActualsMode] Caught error:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
        
        // Handle timeout/abort errors
        if (error.name === 'AbortError') {
          console.error('[useUpdateActualsMode] Request timeout (90s)');
          throw new Error('Requisição expirou. O servidor pode estar iniciando (cold start). Tente novamente em alguns segundos.');
        }
        
        // Handle network errors
        if (error.message === 'Failed to fetch') {
          console.error('[useUpdateActualsMode] Network error - Failed to fetch');
          console.error('[useUpdateActualsMode] This usually means: CORS issue, server not running, or invalid URL');
          console.error('[useUpdateActualsMode] Check browser console Network tab for details');
          throw new Error('Erro de conexão. Verifique sua internet ou tente novamente em alguns segundos (servidor pode estar iniciando).');
        }
        
        // Re-throw other errors
        console.error('[useUpdateActualsMode] Re-throwing error:', error);
        throw error;
      }
    },
    onSuccess: (_, variables) => {
      console.log('[useUpdateActualsMode] Invalidating queries for budget:', variables.budgetId);
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets', variables.companyId] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budget', variables.budgetId] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budget-direct', variables.budgetId] });
      queryClient.invalidateQueries({ queryKey: ['integrated-budgets-direct'] });
      toast.success('Modo de cálculo atualizado!');
    },
    onError: (error: Error) => {
      console.error('[useUpdateActualsMode] Error:', error);
      toast.error(error.message || 'Erro ao atualizar modo');
    },
  });
}
